import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { scrollToId } from "@/lib/api";

const LINKS = [["services", "Services"], ["packages", "Packages"], ["pricing", "Pricing"], ["portfolio", "Portfolio"], ["about", "About"], ["faq", "FAQs"], ["contact", "Contact"]];

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const go = (id) => {
    setOpen(false);
    if (pathname !== "/") { navigate("/"); setTimeout(() => scrollToId(id), 350); }
    else setTimeout(() => scrollToId(id), 60);
  };

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      id="site-header"
      className="fixed inset-x-0 top-0 z-50 border-b border-white/5 bg-[#070B14]/90 px-4 pt-3 pb-3 backdrop-blur-xl"
    >
      <nav className="mx-auto flex max-w-6xl items-center justify-between rounded-full border border-white/10 bg-[#070B14]/75 px-5 py-3 backdrop-blur-xl" data-testid="main-nav">
        <Link to="/" data-testid="nav-logo-link" aria-label="SwayamScale home"><Logo /></Link>
        <div className="hidden items-center gap-7 lg:flex">
          {LINKS.map(([id, label]) => (
            <button key={id} onClick={() => go(id)} data-testid={`nav-link-${id}`}
              className="nav-link text-sm text-slate-300 transition-colors hover:text-white">{label}</button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {user ? (
            <Link to="/dashboard" data-testid="nav-dashboard-button"
              className="hidden items-center gap-2 rounded-full border border-white/15 py-1 pl-1 pr-4 text-sm text-white transition-colors hover:border-[#00F0FF]/60 sm:flex">
              {user.picture ? <img src={user.picture} alt="" className="h-7 w-7 rounded-full" /> : <span className="h-7 w-7 rounded-full bg-[#00F0FF]/20" />}
              Dashboard
            </Link>
          ) : (
            <button onClick={login} data-testid="nav-signin-button"
              className="hidden rounded-full px-4 py-2 text-sm text-slate-200 transition-colors hover:text-white sm:block">Sign in</button>
          )}
          <button onClick={() => go("contact")} data-testid="nav-cta-button"
            className="group hidden items-center gap-1.5 rounded-full bg-[#00F0FF] px-4 py-2 text-sm font-semibold text-[#070B14] transition-transform hover:scale-[1.03] sm:flex">
            Get a Free Consultation <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
          </button>
          <button className="rounded-full p-2 text-white lg:hidden" onClick={() => setOpen((o) => !o)} data-testid="nav-mobile-toggle" aria-label="Menu">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            className="mx-auto mt-2 max-w-6xl rounded-3xl border border-white/10 bg-[#0E1526]/95 p-6 backdrop-blur-xl lg:hidden" data-testid="nav-mobile-menu">
            {LINKS.map(([id, label]) => (
              <button key={id} onClick={() => go(id)} data-testid={`nav-mobile-link-${id}`}
                className="block w-full py-3 text-left font-display text-2xl text-white">{label}</button>
            ))}
            <div className="mt-4 flex flex-col gap-3">
              <button onClick={() => go("contact")} data-testid="nav-mobile-cta" className="rounded-full bg-[#00F0FF] py-3 font-semibold text-[#070B14]">Get a Free Consultation</button>
              {user ? (
                <Link to="/dashboard" onClick={() => setOpen(false)} data-testid="nav-mobile-dashboard" className="flex-1 rounded-full border border-white/15 py-3 text-center text-white">Dashboard</Link>
              ) : (
                <button onClick={login} data-testid="nav-mobile-signin" className="flex-1 rounded-full border border-white/15 py-3 text-white">Sign in with Google</button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
